/* eslint-disable react-hooks/exhaustive-deps */
import { ChevronDownIcon, ChevronUpIcon } from "@chakra-ui/icons";
import {
  Box,
  Checkbox,
  CheckboxGroup,
  Flex,
  HStack,
  Portal,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";

export function CheckboxListMultiSelect({
  value = "",
  onChange,
  direction = "vertical",
  height = "120px",
  disabled,
  options,
  load,
  loading,
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const normalizedValue = value ? value.split(",").map((v) => v.trim()) : [];

  const isVertical = direction === "vertical";
  const Wrapper = isVertical ? VStack : HStack;

  const wrapperProps = isVertical
    ? { spacing: 3, align: "stretch" }
    : { spacing: 4, wrap: "wrap" };

  const [fallbackOptions, setFallbackOptions] = useState([]);

  useEffect(() => {
    if (!normalizedValue.length) return;

    const missing = normalizedValue
      .filter((v) => !options.some((o) => o.value === v))
      .map((v) => ({ value: v, label: v }));

    setFallbackOptions(missing);
  }, [normalizedValue, options]);

  const mergedOptions = [...options, ...fallbackOptions];

  const handleChange = (vals) => {
    onChange(vals.join(",")); // <-- always CSV
  };

  const handleToggle = () => {
    if (!isExpanded) {
      load();
    }
    setIsExpanded(!isExpanded);
  };

  // Close when clicking outside
  useEffect(() => {
    if (!isExpanded) return;

    const handleClickOutside = (event) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsExpanded(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isExpanded]);

  // Get selected options for collapsed view
  const selectedOptions = mergedOptions.filter((opt) =>
    normalizedValue.includes(opt.value)
  );

  const displayText =
    selectedOptions.length > 0
      ? selectedOptions.map((o) => o.label).join(", ")
      : "Select options...";

  return (
    <Box position="relative">
      {/* Collapsed view - shows selected values */}
      <Flex
        ref={triggerRef}
        borderWidth="1px"
        borderRadius="md"
        p={3}
        cursor={disabled ? "not-allowed" : "pointer"}
        opacity={disabled ? 0.6 : 1}
        onClick={disabled ? undefined : handleToggle}
        justifyContent="space-between"
        alignItems="center"
        bg={isExpanded ? "gray.50" : "white"}
        _hover={disabled ? {} : { bg: "gray.50" }}
      >
        <Text
          fontSize="sm"
          color={selectedOptions.length > 0 ? "inherit" : "gray.500"}
          noOfLines={1}
          flex={1}
        >
          {displayText}
        </Text>
        {isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
      </Flex>

      {/* Expanded view - large centered popup */}
      {isExpanded && (
        <Portal>
          {/* Backdrop overlay */}
          <Box
            position="fixed"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="blackAlpha.300"
            zIndex={9998}
            onClick={() => setIsExpanded(false)}
          />

          {/* Centered popup */}
          <Box
            ref={dropdownRef}
            position="fixed"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            width="600px"
            maxWidth="90vw"
            maxH="70vh"
            overflowY="auto"
            borderWidth="1px"
            borderRadius="lg"
            p={6}
            bg="white"
            boxShadow="2xl"
            zIndex={9999}
          >
            <Text fontSize="lg" fontWeight="semibold" mb={4}>
              Select Options
            </Text>
            <CheckboxGroup
              value={normalizedValue}
              onChange={handleChange}
              isDisabled={disabled}
            >
              <Wrapper {...wrapperProps}>
                {loading ? (
                  <Box fontSize="sm" color="gray.500">
                    Loading...
                  </Box>
                ) : (
                  <Flex alignItems="center" flexWrap="wrap" gap={5}>
                    {mergedOptions.map((opt) => (
                      <Checkbox key={opt.value} value={opt.value}>
                        {opt.label}
                      </Checkbox>
                    ))}
                  </Flex>
                )}
              </Wrapper>
            </CheckboxGroup>
          </Box>
        </Portal>
      )}
    </Box>
  );
}
